import { isDefined, isInt } from 'class-validator';
import { DateTime } from 'luxon';

import * as IntervalLib from './interval';
import { insertStringIntoString, leftPadString } from './js-helpers';
import { objectKeys } from './js-helpers/obj';
import { NovaDate } from './nova-date';
import { Nullable } from './globals';

export interface IClockSpan {
  start: string; // 'H:mm'
  end: string; // 'H:mm'
}

export interface IClockTime {
  text: string; // e.g. '12:00 am'
  value: string; // e.g. '23:59'
}

export const ClockSpanFormat = 'H:mm';
export const ClockSpanMultiFormats = [ClockSpanFormat, 'HH:mm'];
export const TwentyFourHourClockSpan: IClockSpan = { start: '0:00', end: '23:59' };

export function splitClockIntoUnitsOfTime(clockTime: string): {
  hour: number;
  minute: number;
} {
  const [hour, minute] = clockTime.split(':').map(Number);
  return { hour, minute };
}

export function getTwentyFourHourClockSpan(): IClockSpan {
  return { start: '0:00', end: '23:59' };
}

export type IClockSpanList = IClockSpan[];

const validClockRegex = /^\d{1,2}:\d{2}$/u;

export function clockToDateTime(clock: string): DateTime | null {
  if (typeof clock !== 'string' || !validClockRegex.test(clock) || clock?.startsWith('24:')) {
    return null;
  }
  const { hour, minute } = splitClockIntoUnitsOfTime(clock);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return DateTime.fromObject({ hour, minute }, { zone: 'utc' });
}

export function clockIsValid(clock: string): boolean {
  return typeof clock === 'string' && clockToDateTime(clock) !== null;
}

export function validateClockSpan(i: IClockSpan): null | string {
  if (i === null || typeof i === 'undefined' || typeof i !== 'object') {
    return `ClockSpan incorrect type: '${i}'`;
  }

  const keys = objectKeys(i);
  const validKeys = ['start', 'end'] as const;

  for (const key of validKeys) {
    if (!keys.includes(key)) {
      return `ClockSpan missing key: '${key}'`;
    }
  }

  for (const key of keys) {
    if (!validKeys.includes(key)) {
      return `ClockSpan has an invalid key: '${key}'`;
    }

    const val = i[key];
    if (typeof val !== 'string') {
      return `ClockSpan '${key}' should be a string: '${val}'`;
    }

    if (!clockIsValid(val)) {
      return `ClockSpan '${key}' should be a clock time (format ${ClockSpanFormat}): '${val}'`;
    }
  }

  const startDt = clockToDateTime(i.start);
  const endDt = clockToDateTime(i.end);
  if (startDt === null || endDt === null) {
    // eslint-disable-next-line quotes
    return `ClockSpan 'start' or 'end' is not a valid clock time`;
  }
  if (startDt.toMillis() > endDt.toMillis()) {
    return `ClockSpan 'start' (${i.start}) is later than 'end' (${i.end})`;
  }

  return null;
}

export function clockTo2001Format(clock: string, dayOfWeek: number): Date {
  if (!Number.isInteger(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) {
    throw new Error(`Core: clockTo2001Format(): Invalid dayOfWeek ${dayOfWeek} or clock ${clock}`);
  }

  const clockDt = clockToDateTime(clock);
  if (clockDt === null) {
    throw new Error(`Core: clockTo2001Format(): Invalid dayOfWeek ${dayOfWeek} or clock ${clock}`);
  }

  const timeStr = `${String(clockDt.hour).padStart(2, '0')}:${String(clockDt.minute).padStart(
    2,
    '0'
  )}`;
  const date = new Date(`2001-01-0${dayOfWeek}T${timeStr}:00.000Z`);

  if (isNaN(date.getTime())) {
    throw new Error(`Core: clockTo2001Format(): Invalid dayOfWeek ${dayOfWeek} or clock ${clock}`);
  }

  return date;
}

export function clockSpanToInterval(
  cs: IClockSpan,
  startDayOfWeek: number,
  endDayOfWeek?: number
): IntervalLib.ITimeInterval {
  return IntervalLib.fromDates(
    clockTo2001Format(cs.start, startDayOfWeek),
    clockTo2001Format(cs.end, endDayOfWeek || startDayOfWeek)
  );
}

export function clocksDontOverlap(cs1: IClockSpan, cs2: IClockSpan, dayOfWeek = 1): boolean {
  return IntervalLib.doesntOverlap(
    clockSpanToInterval(cs1, dayOfWeek),
    clockSpanToInterval(cs2, dayOfWeek)
  );
}

export function clocksOverlap(cs1: IClockSpan, cs2: IClockSpan, dayOfWeek = 1): boolean {
  return IntervalLib.overlaps(
    clockSpanToInterval(cs1, dayOfWeek),
    clockSpanToInterval(cs2, dayOfWeek)
  );
}

export function mergeAdjacentSpans(
  clockSpans: IClockSpanList,
  gapThreshold_ms = 1000
): IClockSpanList {
  const intervals = IntervalLib.sortByStart(clockSpanListToIntervalList(clockSpans));

  if (gapThreshold_ms && typeof gapThreshold_ms !== 'number') {
    throw new Error(`Core: mergeAdjacentSpans(): invalid gapThreshold_ms ${gapThreshold_ms}`);
  }
  const mergedIntervals = IntervalLib.mergeSmallGaps(intervals, gapThreshold_ms);

  return intervalListToClockSpanList(mergedIntervals);
}

export function intervalToClockSpan(interval: IntervalLib.ITimeInterval): IClockSpan {
  if (!interval?.start || !interval?.end) {
    throw new Error('Core: intervalToClockSpan(): invalid interval');
  }
  const dtStart = new NovaDate(interval.start);
  const dtEnd = new NovaDate(interval.end);
  if (!dtStart.isValid() || !dtEnd.isValid()) {
    throw new Error('Core: intervalToClockSpan(): invalid interval');
  }
  const clockSpan = {
    start: dtStart.format(ClockSpanFormat, { tz: 'utc' }),
    end: dtEnd.format(ClockSpanFormat, { tz: 'utc' })
  };
  const validateClockSpanMessage = validateClockSpan(clockSpan);
  if (validateClockSpanMessage === null) {
    return clockSpan;
  }
  throw new Error(`Core: intervalToClockSpan(): ${validateClockSpanMessage}`);
}

export function clockSpanListToIntervalList(
  clockSpanList: IClockSpanList,
  dayOfWeek = 1
): IntervalLib.ITimeIntervalList {
  return clockSpanList.map(span => clockSpanToInterval(span, dayOfWeek));
}

export function intervalListToClockSpanList(
  intervalList: IntervalLib.ITimeIntervalList
): IClockSpanList {
  return intervalList.map(interval => intervalToClockSpan(interval));
}

export function makeFullDayClockOptions(
  intervalMinutesLength = 30,
  useMilitaryTime = false
): Array<IClockTime> {
  if (!Number.isInteger(intervalMinutesLength) || intervalMinutesLength < 1) {
    throw new Error('intervalMinutesLength should be an integer greater than or equal 1');
  }

  const items: IClockTime[] = [];
  let current = clockToDateTime('0:00');
  const endLimit = clockToDateTime('23:59');

  if (current === null || endLimit === null) {
    throw new Error('Core: makeFullDayClockOptions(): internal clock parse error');
  }

  while (current.toMillis() <= endLimit.toMillis()) {
    const text = useMilitaryTime
      ? current.toFormat('H:mm')
      : current.toFormat('h:mm a').toLowerCase();
    items.push({
      text,
      value: current.toFormat('H:mm')
    });
    current = current.plus({ minutes: intervalMinutesLength });
  }
  items.push({ text: useMilitaryTime ? '0:00' : '12:00 am', value: '23:59' });

  return items;
}

export function getFullDayClockOptionsGroupedByHour(intervalMinutes = 30, useMilitaryTime = false) {
  const options = [...makeFullDayClockOptions(intervalMinutes, useMilitaryTime)];
  return options;
}

export function clockAsInt(clockTime: string): number {
  return parseInt(clockTime.replace(':', ''), 10);
}

export const intToClock = (integer: number): string => {
  if ((!integer && integer !== 0) || !isInt(integer) || integer >= 2400 || integer < 0) {
    throw new Error(`Invalid integer conversion '${integer}'`);
  }
  const timeAsInt = integer.toString();
  return `${timeAsInt.slice(0, -2)}:${timeAsInt.slice(-2)}`;
};

export function minutesSinceMidnightToClock(minutes: number, leadingZeroHour = false): string {
  let hours: string | number = Math.floor(minutes / 60);
  if (leadingZeroHour) {
    hours = leftPadString(hours, 2, '0', 2);
  }
  let remainder = leftPadString(minutes % 60, 2, '0', 2);
  if (hours === 24) {
    hours = 23;
    remainder = '59';
  }
  return `${hours}:${remainder}`;
}

export function formatClockTime(
  time: string | number,
  isMilitaryTimeEnabled?: boolean
): Nullable<string> {
  if (!isDefined(time) || (!isInt(time) && !time)) {
    return null;
  }

  function formatIntoClockTime(timeValue: string | number): string {
    let timeStr = String(timeValue);
    if (!timeStr.includes(':') && timeStr.length > 2) {
      timeStr = insertStringIntoString(timeStr, ':', 2);
    }
    return timeStr;
  }
  function getTimeParts(clock: string): { hour: string; minute: string } {
    const parts = clock.split(':');
    const hour = parts[0];
    const minute = parts?.[1] ?? '0';
    return { hour, minute };
  }
  function padHour(hour: string): string {
    const hourAsInt = parseInt(hour, 10);
    const hourNeedsPadding = hourAsInt < 10 && hour.substring(0, 1) !== '0';
    return hourNeedsPadding ? pad(hour) : hour;
  }
  function handleCarryOver(hour: string, minute: string): { hour: string; minute: string } {
    hour = padHour(hour);
    const hourAsInt = parseInt(hour, 10);
    const minuteAsInt = parseInt(minute, 10);

    const hourNeedsCarryOver =
      (!isMilitaryTimeEnabled && hourAsInt > 12) || (isMilitaryTimeEnabled && hourAsInt > 23);
    if (hourNeedsCarryOver) {
      const carryOver = hour.substring(1);
      hour = pad(hour.substring(0, 1));
      if (minuteAsInt > 59) {
        minute = minute.substring(0, 1);
      }
      minute = carryOver + minute;
    }
    return { hour, minute };
  }
  function finalizeMinute(minute: string): string {
    const minuteAsInt = parseInt(minute, 10);

    if (minuteAsInt < 10) {
      minute = pad(minute);
    }

    if (minuteAsInt > 59) {
      minute = '59';
    }
    return minute;
  }
  function pad(str: string, direction: string = 'left', padStr: string = '0'): string {
    return direction === 'left' ? padStr + str : str + padStr;
  }

  const formatted = formatIntoClockTime(time);
  let { hour, minute } = getTimeParts(formatted);

  ({ hour, minute } = handleCarryOver(hour, minute));
  minute = finalizeMinute(minute);

  return `${hour.slice(-2)}:${minute.slice(-2)}`;
}
